import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { clampPercentage } from '../utils/scoringUtils';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import {
  ArrowRight,
  BarChart3,
  CheckCircle,
  Trophy
} from 'lucide-react';

interface ActivityCard {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
}

interface RecommendationCard {
  id: string;
  title: string;
  description: string;
  image: string;
  buttonText: string;
}

const DashboardPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Chart modal state
  const [showChartModal, setShowChartModal] = useState(false);
  const [chartType, setChartType] = useState<'line' | 'bar' | 'pie'>('line');
  const [chartData, setChartData] = useState<any[]>([]);

  // Font selection state


  // Fetch chart data on component mount
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          return;
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        // Enhanced color palette for better visibility and contrast
        const colors = [
          '#8B5CF6', // Purple (primary theme color)
          '#10B981', // Emerald green
          '#F59E0B', // Amber
          '#EF4444', // Red
          '#3B82F6', // Blue
          '#EC4899', // Pink
          '#84CC16', // Lime green
          '#F97316', // Orange
        ];

        const assessmentConfigs = [
          {
            endpoint: API_CONFIG.ENDPOINTS.PHYSICAL_HEALTH_LATEST,
            name: 'Physical',
            color: colors[0],
            maxScore: 100,
            scorePath: 'scores.overall',
            dataPath: 'data',
            assessmentType: 'physical'
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.SPIRITUAL}/latest?type=fasdl`,
            name: 'Spiritual',
            color: colors[1],
            maxScore: 50,
            scorePath: 'assessmentData.scores.overall',
            dataPath: 'data',
            assessmentType: 'spiritual'
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.NUTRITIONAL}/latest`,
            name: 'Nutritional',
            color: colors[2],
            maxScore: 100,
            scorePath: 'scores.overall',
            dataPath: 'data',
            assessmentType: 'nutritional'
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL}/latest`,
            name: 'Emotio-Psycho-Rational',
            color: colors[3],
            maxScore: 100,
            scorePath: 'assessment.scores.overall',
            dataPath: 'data',
            assessmentType: 'mental-health',
            history_endpoint: `${API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL}/history`
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.LEGENDARIAL}/history`,
            name: 'Legendarial',
            color: colors[4],
            maxScore: 100,
            scorePath: 'scores.overall',
            dataPath: 'data',
            assessmentType: 'legendarial'
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.RELIGIO_TRADITIO_CULTURAL}/history`,
            name: 'Religio-Traditio-Cultural',
            color: colors[5],
            maxScore: 100,
            scorePath: 'scores.overall',
            dataPath: 'data',
            assessmentType: 'religio'
          },
          {
            endpoint: API_CONFIG.ENDPOINTS.SOCIO_RELATIONAL_LATEST,
            name: 'Socio-Relational',
            color: colors[6],
            maxScore: 20,
            scorePath: 'scores.socioRelationalScore',
            dataPath: 'data',
            assessmentType: 'socio-relational'
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.FINANCIAL}/latest`,
            name: 'Financial',
            color: colors[7],
            maxScore: 80,
            scorePath: 'scores.overall',
            fallbackScorePath: 'scores.categoryScore',
            dataPath: 'data',
            assessmentType: 'financial'
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.ENVIRONMENTAL}/latest`,
            name: 'Environmental',
            color: '#84CC16',
            maxScore: 100,
            scorePath: 'scores.transformedScore',
            dataPath: 'data',
            assessmentType: 'environmental'
          },
          {
            endpoint: `${API_CONFIG.ENDPOINTS.OCCUPATIONAL}/history`,
            name: 'Occupational',
            color: '#F97316',
            maxScore: 100,
            scorePath: 'scores.overall',
            dataPath: 'data',
            assessmentType: 'occupational'
          }
        ];

        // Fetch all assessments concurrently with proper handling for combined assessments
        const fetchPromises = assessmentConfigs.map(async (config) => {
          try {
            // Special handling for combined assessments
            if (config.assessmentType === 'spiritual') {
              // Combined Spiritual Assessment (FASDL + FACIT-Sp-12)
              const [fasdlResponse, facitResponse] = await Promise.all([
                fetch(`${API_CONFIG.ENDPOINTS.SPIRITUAL}/latest?type=fasdl`, { headers, credentials: 'include' }),
                fetch(`${API_CONFIG.ENDPOINTS.SPIRITUAL}/latest?type=facit-sp-12`, { headers, credentials: 'include' })
              ]);

              let combinedScore = 0;
              if (fasdlResponse.ok && facitResponse.ok) {
                const [fasdlData, facitData] = await Promise.all([
                  fasdlResponse.json(),
                  facitResponse.json()
                ]);

                if (fasdlData.success && fasdlData.data?.assessmentData?.scores?.overall !== undefined) {
                  const fasdlPercentage = Math.round(clampPercentage((fasdlData.data.assessmentData.scores.overall / 50) * 100));
                  combinedScore = fasdlPercentage;
                }
                if (facitData.success && facitData.data?.assessmentData?.scores?.overall !== undefined) {
                  const facitPercentage = Math.round(clampPercentage((facitData.data.assessmentData.scores.overall / 48) * 100));
                  combinedScore = combinedScore > 0 ? Math.round((combinedScore + facitPercentage) / 2) : facitPercentage;
                }
              }

              if (combinedScore > 0) {
                const level = combinedScore >= 75 ? 'High' : combinedScore >= 50 ? 'Moderate' : 'Low';
                return {
                  name: config.name,
                  percentage: clampPercentage(combinedScore),
                  level,
                  fill: config.color,
                  score: `${combinedScore}%`
                };
              }
              return null;
            }

            if (config.assessmentType === 'legendarial') {
              // Combined Legendarial Assessment (SWLS + PIL)
              const response = await fetch(config.endpoint, { headers, credentials: 'include' });
              if (!response.ok) return null;

              const apiResponse = await response.json();
              if (!apiResponse.success || !apiResponse.data) return null;

              // Legendarial history returns data: { assessments }
              const assessments = apiResponse.data.assessments || [];
              const swlsAssessment = assessments.find((a: any) => a.selectedCategory === 1);
              const pilAssessment = assessments.find((a: any) => a.selectedCategory === 2);

              const swlsCompleted = swlsAssessment && swlsAssessment.scores && swlsAssessment.results;
              const pilCompleted = pilAssessment && pilAssessment.scores && pilAssessment.results;

              let combinedScore = 0;
              if (swlsCompleted && pilCompleted) {
                const swlsPercentage = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
                const pilPercentage = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
                combinedScore = Math.round((swlsPercentage + pilPercentage) / 2);
              } else if (swlsCompleted) {
                combinedScore = Math.round(clampPercentage(((swlsAssessment.scores.swlsTotal || 0) / 35) * 100));
              } else if (pilCompleted) {
                combinedScore = Math.round(clampPercentage(((pilAssessment.scores.pilTotal || 0) / 70) * 100));
              }

              if (combinedScore > 0) {
                const level = combinedScore >= 75 ? 'High' : combinedScore >= 50 ? 'Moderate' : 'Low';
                return {
                  name: config.name,
                  percentage: clampPercentage(combinedScore),
                  level,
                  fill: config.color,
                  score: `${combinedScore}%`
                };
              }
              return null;
            }

            if (config.assessmentType === 'religio') {
              // Combined Religio-Traditio-Cultural Assessment
              const response = await fetch(config.endpoint, { headers, credentials: 'include' });
              if (!response.ok) return null;

              const apiResponse = await response.json();
              if (!apiResponse.success || !apiResponse.data) return null;

              // Religio history returns data: assessments (array directly)
              const assessments = apiResponse.data || [];
              const briefRcopeAssessment = assessments.find((a: any) => a.selectedCategory === 1);
              const cvscaleAssessment = assessments.find((a: any) => a.selectedCategory === 2);
              const tvasAssessment = assessments.find((a: any) => a.selectedCategory === 3);

              const briefRcopeCompleted = briefRcopeAssessment && briefRcopeAssessment.scores && briefRcopeAssessment.results;
              const cvscaleCompleted = cvscaleAssessment && cvscaleAssessment.scores && cvscaleAssessment.results;
              const tvasCompleted = tvasAssessment && tvasAssessment.scores && tvasAssessment.results;

              const percentages = [];
              if (briefRcopeCompleted) percentages.push(Math.round(clampPercentage((((briefRcopeAssessment.scores.category1 || briefRcopeAssessment.scores.prcTotal || 0) - (briefRcopeAssessment.scores.nrcTotal || 0) + 21) / 42) * 100)));
              if (cvscaleCompleted) percentages.push(Math.round(clampPercentage(((cvscaleAssessment.scores.cvscaleTotal || 0) / 5) * 100)));
              if (tvasCompleted) percentages.push(Math.round(clampPercentage(((tvasAssessment.scores.respectForTraditionTotal || 0) / 50) * 100)));

              if (percentages.length > 0) {
                const combinedScore = clampPercentage(Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length));
                const level = combinedScore >= 75 ? 'High' : combinedScore >= 50 ? 'Moderate' : 'Low';
                return {
                  name: config.name,
                  percentage: combinedScore,
                  level,
                  fill: config.color,
                  score: `${combinedScore}%`
                };
              }
              return null;
            }

            if (config.assessmentType === 'occupational') {
              // Combined Occupational Assessment with Employee Engagement logic
              const response = await fetch(config.endpoint, { headers, credentials: 'include' });
              if (!response.ok) return null;

              const apiResponse = await response.json();
              if (!apiResponse.success || !apiResponse.data) return null;

              // Occupational history returns data: assessments (array directly)
              const assessments = apiResponse.data || [];
              const cbiAssessment = assessments.find((a: any) => a.selectedCategory === 1);
              const uwesAssessment = assessments.find((a: any) => a.selectedCategory === 2);
              const employeeEngagementAssessment = assessments.find((a: any) => a.selectedCategory === 3);

              const cbiCompleted = cbiAssessment && cbiAssessment.scores && cbiAssessment.results;
              const uwesCompleted = uwesAssessment && uwesAssessment.scores && uwesAssessment.results;
              const employeeEngagementCompleted = employeeEngagementAssessment && employeeEngagementAssessment.scores && employeeEngagementAssessment.results;

              const percentages = [];
              if (cbiCompleted) percentages.push(Math.round(clampPercentage(((cbiAssessment.scores.cbiTotal || 0) / 100) * 100)));
              if (uwesCompleted) percentages.push(Math.round(clampPercentage((1 - ((uwesAssessment.scores.uwesTotal || 0) / 6)) * 100)));
              // Employee Engagement: invert to show burnout (higher engagement = lower burnout percentage)
              if (employeeEngagementCompleted) percentages.push(Math.round(clampPercentage((1 - ((employeeEngagementAssessment.scores.engagementTotal || 0) / 5)) * 100)));

              if (percentages.length > 0) {
                const combinedScore = clampPercentage(Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length));
                const level = combinedScore >= 75 ? 'High' : combinedScore >= 50 ? 'Moderate' : 'Low';
                return {
                  name: config.name,
                  percentage: combinedScore,
                  level,
                  fill: config.color,
                  score: `${combinedScore}% (Combined)`
                };
              }
              return null;
            }

            if (config.assessmentType === 'mental-health') {
              // Combined Mental Health Assessment (DASS-21, PHQ-9, GAD-7, Gender Sensitivity)
              // Fetch history to get all assessments
              const historyEndpoint = (config as any).history_endpoint || `${API_CONFIG.ENDPOINTS.EMOTIO_PSYCHO_RATIONAL}/history`;
              const response = await fetch(historyEndpoint, { headers, credentials: 'include' });
              if (!response.ok) return null;

              const apiResponse = await response.json();
              if (!apiResponse.success || !apiResponse.data) return null;

              const mhHistory = apiResponse.data || [];
              
              // Helper to get latest by selectedCategory
              const getLatestByType = (assessments: any[], category: number) => {
                return assessments
                  .filter(a => a.selectedCategory === category)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
              };

              const dassLatest = getLatestByType(mhHistory, 1);
              const phqLatest = getLatestByType(mhHistory, 2);
              const gadLatest = getLatestByType(mhHistory, 3);
              const genderLatest = getLatestByType(mhHistory, 4);

              let dassPercent = null, phqPercent = null, gadPercent = null, genderPercent = null;
              
              if (dassLatest && dassLatest.scores) {
                dassPercent = Math.round(clampPercentage((dassLatest.scores.overall ?? 0) / 42 * 100));
              }
              if (phqLatest && phqLatest.scores) {
                phqPercent = Math.round(clampPercentage((phqLatest.scores.overall || 0) / 27 * 100));
              }
              if (gadLatest && gadLatest.scores) {
                gadPercent = Math.round(clampPercentage((gadLatest.scores.overall || 0) / 21 * 100));
              }
              if (genderLatest && genderLatest.scores) {
                // Invert: higher raw means lower percent
                genderPercent = Math.round(clampPercentage(100 - ((genderLatest.scores.overall || 0) / 20 * 100)));
              }

              // Only average the assessments that were actually taken (don't include untaken as 0%)
              const percentages = [];
              if (dassPercent !== null) percentages.push(dassPercent);
              if (phqPercent !== null) percentages.push(phqPercent);
              if (gadPercent !== null) percentages.push(gadPercent);
              if (genderPercent !== null) percentages.push(genderPercent);

              let combinedScore = 0;
              if (percentages.length > 0) {
                combinedScore = Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length);
                combinedScore = clampPercentage(combinedScore);
              }

              if (combinedScore > 0 || percentages.length > 0) {
                const level = combinedScore >= 75 ? 'High' : combinedScore >= 50 ? 'Moderate' : 'Low';
                return {
                  name: config.name,
                  percentage: combinedScore,
                  level,
                  fill: config.color,
                  score: `${combinedScore}%`
                };
              }
              return null;
            }

            if (config.assessmentType === 'socio-relational') {
              // Combined Socio-Relational Assessment (Gender Sensitivity STAR + MSPSS)
              const response = await fetch(`${API_CONFIG.ENDPOINTS.SOCIO_RELATIONAL_LATEST}/history`, { headers, credentials: 'include' });
              if (!response.ok) return null;

              const apiResponse = await response.json();
              if (!apiResponse.success || !apiResponse.data) return null;

              const assessments = apiResponse.data || [];
              
              // Get latest of each subcategory
              const genderSensitivityItem = assessments
                .filter((a: any) => a.selectedCategory === 1 && a.scores && a.results)
                .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
              
              const mspssItem = assessments
                .filter((a: any) => a.selectedCategory === 2 && a.scores && a.results)
                .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];

              const percentages = [];
              
              // Gender Sensitivity STAR: max 20
              if (genderSensitivityItem) {
                const genderRaw = genderSensitivityItem.scores?.socioRelationalScore || 0;
                const genderPercent = Math.round((genderRaw / 20) * 100);
                percentages.push(genderPercent);
              }
              
              // MSPSS: max 7 (mean score)
              if (mspssItem) {
                const mspssRaw = mspssItem.scores?.socioRelationalScore || 0;
                const mspssPercent = Math.round((mspssRaw / 7) * 100);
                percentages.push(mspssPercent);
              }

              if (percentages.length > 0) {
                const combinedScore = clampPercentage(Math.round(percentages.reduce((sum, p) => sum + p, 0) / percentages.length));
                const level = combinedScore >= 75 ? 'High' : combinedScore >= 50 ? 'Moderate' : 'Low';
                return {
                  name: config.name,
                  percentage: combinedScore,
                  level,
                  fill: config.color,
                  score: `${combinedScore}%`
                };
              }
              return null;
            }

            // Standard single assessment handling
            const response = await fetch(config.endpoint, { headers, credentials: 'include' });
            if (!response.ok) {
              console.error(`Error fetching ${config.name}:`, response.status, response.statusText);
              return null;
            }

            const apiResponse = await response.json();
            if (!apiResponse.success) {
              return null;
            }

            if (!apiResponse.data) {
              return null;
            }

            // Handle different data structures based on assessment type
            let assessmentData;
            if (config.assessmentType === 'mental-health') {
              // Emotio-Psycho-Rational has data: { assessment: ... }
              assessmentData = apiResponse.data.assessment;
            } else {
              // Most others have data: assessment directly
              assessmentData = apiResponse.data;
            }

            if (!assessmentData) {
              return null;
            }

            // Extract score using the scorePath, with fallback for assessments that have multiple score paths
            let score = config.scorePath.split('.').reduce((obj: any, key: string) => obj?.[key], assessmentData) || 0;
            
            // If primary score is 0 and there's a fallback path, try it
            if (score === 0 && (config as any).fallbackScorePath) {
              score = (config as any).fallbackScorePath.split('.').reduce((obj: any, key: string) => obj?.[key], assessmentData) || 0;
            }

            // Extract level
            const level = assessmentData.results?.level || 'Unknown';

            const percentage = clampPercentage(Math.round((score / config.maxScore) * 100));

            return {
              name: config.name,
              percentage,
              level,
              fill: config.color,
              score: `${score}/${config.maxScore}`
            };
          } catch (error) {
            console.error(`Error fetching ${config.name}:`, error);
            return null;
          }
        });

        // Wait for all fetches to complete
        const results = await Promise.all(fetchPromises);

        // Filter out null results and collect valid data
        const chartData = results.filter(result => result !== null);

        setChartData(chartData);

      } catch (error) {
        console.error('Error fetching chart data:', error);
        // Set sample data as fallback
        setChartData([
          {
            name: 'Mental Health',
            percentage: 75,
            level: 'Moderate',
            fill: '#8B5CF6',
            score: '15/20'
          },
          {
            name: 'Physical Health',
            percentage: 90,
            level: 'High',
            fill: '#10B981',
            score: '18/20'
          },
          {
            name: 'Spiritual',
            percentage: 65,
            level: 'Moderate',
            fill: '#F59E0B',
            score: '32/50'
          },
          {
            name: 'Nutritional',
            percentage: 80,
            level: 'High',
            fill: '#EF4444',
            score: '80/100'
          }
        ]);
      }
    };

    fetchChartData();
  }, [user]);

  const activities: ActivityCard[] = [
    {
      id: 'meditation',
      title: 'Morning Meditation',
      description: 'Start your day with a calming session.',
      image: 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      category: 'mental'
    },
    {
      id: 'yoga',
      title: 'Yoga Flow',
      description: 'Improve flexibility and find inner peace.',
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      category: 'physical'
    },
    {
      id: 'running',
      title: 'Outdoor Run',
      description: 'Enjoy a refreshing run outdoor.',
      image: require('../assets/outdoor-run-banner.jpg'),
      category: 'physical'
    },
    {
      id: 'sleep',
      title: 'Sleep Meditation',
      description: 'Relax and unwind with soothing sleep meditations & stories.',
      image: require('../assets/sleep-meditation.webp'),
      category: 'relaxation'
    }
  ];

  const recommendations: RecommendationCard[] = [
    {
      id: 'meditation-rec',
      title: 'Meditation',
      description: 'Find your inner peace with guided meditation sessions.',
      image: 'https://images.unsplash.com/photo-1593811167562-9cef47bfc4d7?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      buttonText: 'Start'
    },
    {
      id: 'sleep-rec',
      title: 'Sleep',
      description: 'Improve your sleep quality with relaxing sounds and sleep stories.',
      image: require('../assets/sleep-meditation.webp'),
      buttonText: 'Explore'
    }
  ];

  // Fetch recently completed session activities from backend API
  const [recentSessions, setRecentSessions] = useState<any[]>([]);

  useEffect(() => {
    const fetchRecentSessions = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/completed-sessions/recent`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.sessions)) {
          setRecentSessions(data.sessions);
        }
      } catch (err) {
        console.error('Failed to fetch recent sessions:', err);
      }
    };
    fetchRecentSessions();
    window.addEventListener('focus', fetchRecentSessions);
    return () => {
      window.removeEventListener('focus', fetchRecentSessions);
    };
  }, []);

  // Chart functions
  const renderChart = (chartData: any[]) => {
    const commonProps = {
      data: chartData,
      margin: {
        top: 20,
        right: 30,
        left: 20,
        bottom: 60,
      }
    };

    const commonTooltipFormatter = (value: any, name: any, props: any) => [
      `${value}% (${props.payload.score})`,
      props.payload.level
    ];

    const commonTooltipLabelFormatter = (label: any) => `Assessment: ${label}`;

    const commonTooltipStyle = {
      backgroundColor: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    };

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                stroke="#374151"
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                fontSize={12}
                stroke="#374151"
              />
              <Tooltip
                formatter={commonTooltipFormatter as any}
                labelFormatter={commonTooltipLabelFormatter as any}
                contentStyle={commonTooltipStyle}
              />
              <Bar
                dataKey="percentage"
                radius={[4, 4, 0, 0]}
                stroke="#fff"
                strokeWidth={1}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart {...commonProps}>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                dataKey="percentage"
                label={({ name, value }: any) => Number(value) > 5 ? `${name}: ${value}%` : ''}
                labelLine={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip
                formatter={commonTooltipFormatter as any}
                labelFormatter={commonTooltipLabelFormatter as any}
                contentStyle={commonTooltipStyle}
              />
              <Legend
                formatter={(value) => (
                  <span style={{ fontWeight: 'bold' }}>
                    {value}
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        );

      default: // line chart
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                stroke="#374151"
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(value) => `${value}%`}
                fontSize={12}
                stroke="#374151"
              />
              <Tooltip
                formatter={commonTooltipFormatter as any}
                labelFormatter={commonTooltipLabelFormatter as any}
                contentStyle={commonTooltipStyle}
              />
              <Line
                type="monotone"
                dataKey="percentage"
                stroke="#8b5cf6"
                strokeWidth={3}
                dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 6 }}
                activeDot={{ r: 8, stroke: '#8b5cf6', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  // Calculate lowest and highest scores from chart data
  // Takes into account that some assessments have inverted meaning (low score = good)
  const getLowestAndHighestScores = () => {
    if (chartData.length === 0) {
      return {
        lowest: { name: 'No data', percentage: 0 },
        highest: { name: 'No data', percentage: 0 }
      };
    }

    // Assessments where LOW percentage is GOOD (inverted meaning)
    const invertedMeaningAssessments = ['Emotio-Psycho-Rational', 'Occupational'];

    // Create a normalized scoring system:
    // For normal assessments: higher % = better wellness
    // For inverted assessments: lower % = better wellness (so we invert for comparison)
    const itemsWithNormalizedScore = chartData.map(item => {
      let normalizedScore = item.percentage;
      
      // For inverted assessments, invert the score for comparison purposes
      if (invertedMeaningAssessments.includes(item.name)) {
        normalizedScore = 100 - item.percentage;
      }
      
      return {
        ...item,
        normalizedScore
      };
    });

    // Find lowest and highest based on normalized scores
    let lowestItem = itemsWithNormalizedScore[0];
    let highestItem = itemsWithNormalizedScore[0];

    itemsWithNormalizedScore.forEach(item => {
      if (item.normalizedScore < lowestItem.normalizedScore) {
        lowestItem = item;
      }
      if (item.normalizedScore > highestItem.normalizedScore) {
        highestItem = item;
      }
    });

    // Remove the normalized score before returning
    const { normalizedScore: _, ...lowestResult } = lowestItem;
    const { normalizedScore: __, ...highestResult } = highestItem;

    return { lowest: lowestResult, highest: highestResult };
  };

  const { lowest, highest } = getLowestAndHighestScores();

  return (
    <div className={`dashboard ${isDarkMode ? 'dark' : ''}`}>
      <div className="dashboard__container">
        {/* Main Content */}
        <main className="dashboard__main">
          <div className="dashboard__content">
            {/* Welcome Section */}
            {(() => {
              return user && (
                <div className="dashboard__welcome">
                  <div className="dashboard__welcome-greeting">
                    <h1 className="dashboard__welcome-title">
                      Welcome back, {user?.profile?.preferredName || user?.profile?.firstName || user?.email?.split('@')[0] || 'User'}!
                    </h1>
                    <p className="dashboard__welcome-subtitle">Let's continue your wellness journey</p>
                  </div>
                </div>
              );
            })()}

            {/* Stats Cards Section - Enhanced Layout */}
            <div className="dashboard__stats-grid">
              <div className="dashboard__stat-card dashboard__stat-card--primary">
                <div className="dashboard__stat-info">
                  <p className="dashboard__stat-label">Best Performance</p>
                  <p className="dashboard__stat-value">{highest.name}</p>
                  <p className="dashboard__stat-score">{highest.percentage}% Score</p>
                </div>
              </div>
              <div className="dashboard__stat-card dashboard__stat-card--secondary">
                <div className="dashboard__stat-info">
                  <p className="dashboard__stat-label">Area to Improve</p>
                  <p className="dashboard__stat-value">{lowest.name}</p>
                  <p className="dashboard__stat-score">{lowest.percentage}% Score</p>
                </div>
              </div>
            </div>

            {/* Good Job Section - Enhanced */}
            <div className="dashboard__achievement-banner">
              <div className="dashboard__achievement-content">
                <div className="dashboard__achievement-icon-wrapper">
                  <Trophy className="dashboard__achievement-icon" size={28} />
                </div>
                <div className="dashboard__achievement-text">
                  <h2 className="dashboard__achievement-title">Excellent Progress!</h2>
                  <p className="dashboard__achievement-message">
                    You're doing amazing on your wellness journey. Keep up the momentum!
                  </p>
                </div>
              </div>
            </div>

            {/* Activities Section - Modern Grid */}
            <section className="dashboard__section">
              <div className="dashboard__section-header">
                <h2 className="dashboard__section-title">Explore Activities</h2>
                <p className="dashboard__section-subtitle">Try something new today</p>
              </div>
              <div className="dashboard__activities">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="dashboard__activity-card"
                    onClick={() => {
                      if (activity.id === 'meditation') {
                        navigate('/panel/dashboard/morning-meditation');
                      }
                      if (activity.id === 'yoga') {
                        navigate('/panel/dashboard/yoga-flow');
                      }
                      if (activity.id === 'running') {
                        navigate('/panel/dashboard/outdoor-run');
                      }
                      if (activity.id === 'sleep') {
                        navigate('/panel/dashboard/sleeping-meditation');
                      }
                    }}
                  >
                    <div className="dashboard__activity-image-wrapper">
                      <img
                        src={activity.image}
                        alt={activity.title}
                        className="dashboard__activity-image"
                      />
                      <div className="dashboard__activity-overlay"></div>
                    </div>
                    <div className="dashboard__activity-content">
                      <h3 className="dashboard__activity-title">{activity.title}</h3>
                      <p className="dashboard__activity-description">{activity.description}</p>
                      <div className="dashboard__activity-action">
                        <span>Explore</span>
                        <ArrowRight size={16} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Recommendations Section - Modern Cards */}
            <section className="dashboard__section">
              <div className="dashboard__section-header">
                <h2 className="dashboard__section-title">Personalized for You</h2>
                <p className="dashboard__section-subtitle">Based on your wellness profile</p>
              </div>
              <div className="dashboard__recommendations">
                {recommendations.map((recommendation) => (
                  <div key={recommendation.id} className="dashboard__recommendation-card">
                    <div
                      className="dashboard__recommendation-image"
                      style={{ backgroundImage: `url(${recommendation.image})` }}
                    >
                      <div className="dashboard__recommendation-overlay"></div>
                    </div>
                    <div className="dashboard__recommendation-content">
                      <h3 className="dashboard__recommendation-title">{recommendation.title}</h3>
                      <p className="dashboard__recommendation-description">
                        {recommendation.description}
                      </p>
                      <button 
                        className="dashboard__recommendation-btn"
                        onClick={() => {
                          if (recommendation.id === 'meditation-rec') {
                            navigate('/panel/dashboard/morning-meditation');
                          }
                          if (recommendation.id === 'sleep-rec') {
                            navigate('/panel/dashboard/sleeping-meditation');
                          }
                        }}
                      >
                        {recommendation.buttonText}
                        <ArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>
      </div>

      {/* Chart Modal */}
      {showChartModal && (
        <div className="modal-overlay" onClick={() => setShowChartModal(false)}>
          <div className="modal-content chart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assessment Overview Chart</h2>
              <div className="chart-type-selector">
                <button
                  className={`btn btn-sm ${chartType === 'line' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setChartType('line')}
                  title="Line Chart"
                >
                  📈 Line
                </button>
                <button
                  className={`btn btn-sm ${chartType === 'bar' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setChartType('bar')}
                  title="Bar Chart"
                >
                  📊 Bar
                </button>
                <button
                  className={`btn btn-sm ${chartType === 'pie' ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => setChartType('pie')}
                  title="Pie Chart"
                >
                  🥧 Pie
                </button>
              </div>
              <button
                className="modal-close"
                onClick={() => setShowChartModal(false)}
              >
                X
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                if (chartData.length === 0) {
                  return (
                    <div className="no-chart-data">
                      <p>No assessment data available to display chart.</p>
                    </div>
                  );
                }

                return (
                  <div className="chart-container">
                    <div className={`chart-wrapper chart-${chartType}`}>
                      {renderChart(chartData)}
                    </div>

                    <div className="chart-legend">
                      <h3>Assessment Results</h3>
                      <div className="legend-items">
                        {chartData.map((item: any, index: number) => (
                          <div key={index} className="legend-item">
                            <div
                              className="legend-color"
                              style={{ backgroundColor: item.fill }}
                            ></div>
                            <div className="legend-text">
                              <span className="legend-name">{item.name}</span>
                              <span className="legend-details">
                                {item.percentage}% ({item.level}) - {item.score}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;

